import type { StaffRole } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy dos componentes soltos na raiz de `components/` (ADR-0699/0700): peças pequenas e
   reusadas em toda a casca autenticada — marca, command palette, id copiável, estados de
   detalhe, tela de login, modal de motivo genérico, fallbacks do router, busca, sidebar,
   toggle de tema e menu do usuário. `admin-layout.tsx`/`locale-toggle.tsx` ficam fora (já
   migrados via `shell.ts`). */
export interface CommonContent {
  readonly brandLogo: {
    readonly adminChip: string;
  };
  readonly commandPalette: {
    readonly srTitle: string;
    readonly placeholder: string;
    readonly ariaLabel: string;
    readonly noResults: string;
  };
  readonly copyableId: {
    readonly defaultNoun: string;
    readonly copiedToast: (noun: string) => string;
    readonly copyErrorToast: (noun: string) => string;
    readonly copiedAriaLabel: (noun: string) => string;
    readonly copyAriaLabel: (noun: string) => string;
  };
  readonly detailPageState: {
    readonly loadingAriaLabel: string;
  };
  readonly loginForm: {
    /** Endereço de exemplo no campo de e-mail — muda de idioma como qualquer copy. */
    readonly emailPlaceholder: string;
    readonly rateLimited: string;
    readonly networkFailure: string;
    readonly invalidCredentials: string;
    readonly unexpectedError: string;
    readonly validationError: string;
    readonly signedIn: string;
    readonly heroTitle: string;
    readonly heroDescription: string;
    readonly restrictedAccess: string;
    readonly formAriaLabel: string;
    readonly heading: string;
    readonly subheading: string;
    readonly emailLabel: string;
    readonly passwordLabel: string;
    readonly showPasswordAriaLabel: string;
    readonly hidePasswordAriaLabel: string;
    readonly submit: string;
    readonly adminChip: string;
  };
  readonly reasonDialog: {
    readonly placeholder: string;
    readonly characterCount: (count: number) => string;
    readonly cancel: string;
  };
  readonly routeFallbacks: {
    readonly errorMessage: string;
    readonly retry: string;
    readonly notFoundTitle: string;
    readonly notFoundDescription: string;
    readonly backToDashboard: string;
  };
  readonly searchInput: {
    readonly defaultPlaceholder: string;
    readonly clearAriaLabel: string;
  };
  readonly sidebarNav: {
    readonly ariaLabel: string;
  };
  readonly themeToggle: {
    readonly ariaLabel: string;
    readonly light: string;
    readonly dark: string;
    readonly system: string;
  };
  readonly userMenu: {
    /* Função (não `Record`) porque `StaffRole` inclui chaves snake_case (`super_admin`/
       `billing_ops`) e `useNamingConvention` rejeita propriedade de objeto snake_case; um
       `switch` com case string-literal não é flagueado. */
    readonly roleLabel: (role: StaffRole) => string;
    readonly signOutAriaLabel: string;
  };
}

const COMMON_CONTENT: Bilingual<CommonContent> = {
  'pt-BR': {
    brandLogo: { adminChip: 'Admin' },
    commandPalette: {
      srTitle: 'Buscar e navegar',
      placeholder: 'Buscar telas…',
      ariaLabel: 'Buscar telas',
      noResults: 'Nenhuma tela encontrada.',
    },
    copyableId: {
      defaultNoun: 'identificador',
      copiedToast: (noun) => `${noun} copiado.`,
      copyErrorToast: (noun) => `Não foi possível copiar o ${noun}.`,
      copiedAriaLabel: (noun) => `${noun} copiado`,
      copyAriaLabel: (noun) => `Copiar ${noun}`,
    },
    detailPageState: { loadingAriaLabel: 'Carregando' },
    loginForm: {
      emailPlaceholder: 'voce@planuze.com.br',
      rateLimited: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.',
      networkFailure: 'Falha de conexão. Verifique sua rede e tente de novo.',
      invalidCredentials: 'Credenciais inválidas.',
      unexpectedError: 'Erro inesperado. Tente novamente.',
      validationError: 'Informe um email válido e uma senha de ao menos 8 caracteres.',
      signedIn: 'Sessão iniciada.',
      heroTitle: 'Backoffice do ecossistema',
      heroDescription:
        'Governança de empresas, publishers, planos, releases e moderação — em um só painel.',
      restrictedAccess: 'Acesso restrito à equipe interna Planuze.',
      formAriaLabel: 'Formulário de acesso',
      heading: 'Acesso ao painel',
      subheading: 'Entre com suas credenciais de operador.',
      emailLabel: 'Email',
      passwordLabel: 'Senha',
      showPasswordAriaLabel: 'Mostrar senha',
      hidePasswordAriaLabel: 'Ocultar senha',
      submit: 'Entrar',
      adminChip: 'Admin',
    },
    reasonDialog: {
      placeholder: 'Descreva o motivo...',
      characterCount: (count) => `${count} caracteres`,
      cancel: 'Cancelar',
    },
    routeFallbacks: {
      errorMessage: 'Algo deu errado ao carregar esta tela.',
      retry: 'Tentar novamente',
      notFoundTitle: 'Página não encontrada',
      notFoundDescription: 'O endereço acessado não existe no backoffice.',
      backToDashboard: 'Voltar ao painel',
    },
    searchInput: {
      defaultPlaceholder: 'Buscar…',
      clearAriaLabel: 'Limpar busca',
    },
    sidebarNav: { ariaLabel: 'Navegação principal' },
    themeToggle: {
      ariaLabel: 'Alternar tema',
      light: 'Claro',
      dark: 'Escuro',
      system: 'Sistema',
    },
    userMenu: {
      roleLabel: (role) => {
        switch (role) {
          case 'super_admin':
            return 'Super Admin';
          case 'support':
            return 'Suporte';
          case 'billing_ops':
            return 'Billing Ops';
          case 'readonly':
            return 'Somente leitura';
        }
      },
      signOutAriaLabel: 'Sair',
    },
  },
  'en-US': {
    brandLogo: { adminChip: 'Admin' },
    commandPalette: {
      srTitle: 'Search and navigate',
      placeholder: 'Search screens…',
      ariaLabel: 'Search screens',
      noResults: 'No screen found.',
    },
    copyableId: {
      defaultNoun: 'identifier',
      copiedToast: (noun) => `${noun} copied.`,
      copyErrorToast: (noun) => `Could not copy the ${noun}.`,
      copiedAriaLabel: (noun) => `${noun} copied`,
      copyAriaLabel: (noun) => `Copy ${noun}`,
    },
    detailPageState: { loadingAriaLabel: 'Loading' },
    loginForm: {
      emailPlaceholder: 'you@planuze.com',
      rateLimited: 'Too many attempts. Wait a few minutes and try again.',
      networkFailure: 'Connection failure. Check your network and try again.',
      invalidCredentials: 'Invalid credentials.',
      unexpectedError: 'Unexpected error. Try again.',
      validationError: 'Enter a valid email and a password of at least 8 characters.',
      signedIn: 'Signed in.',
      heroTitle: 'Ecosystem backoffice',
      heroDescription:
        'Governance of companies, publishers, plans, releases, and moderation — in a single panel.',
      restrictedAccess: 'Access restricted to Planuze internal staff.',
      formAriaLabel: 'Sign-in form',
      heading: 'Panel access',
      subheading: 'Sign in with your operator credentials.',
      emailLabel: 'Email',
      passwordLabel: 'Password',
      showPasswordAriaLabel: 'Show password',
      hidePasswordAriaLabel: 'Hide password',
      submit: 'Sign in',
      adminChip: 'Admin',
    },
    reasonDialog: {
      placeholder: 'Describe the reason...',
      characterCount: (count) => `${count} characters`,
      cancel: 'Cancel',
    },
    routeFallbacks: {
      errorMessage: 'Something went wrong loading this screen.',
      retry: 'Try again',
      notFoundTitle: 'Page not found',
      notFoundDescription: 'The requested address does not exist in the backoffice.',
      backToDashboard: 'Back to dashboard',
    },
    searchInput: {
      defaultPlaceholder: 'Search…',
      clearAriaLabel: 'Clear search',
    },
    sidebarNav: { ariaLabel: 'Main navigation' },
    themeToggle: {
      ariaLabel: 'Toggle theme',
      light: 'Light',
      dark: 'Dark',
      system: 'System',
    },
    userMenu: {
      roleLabel: (role) => {
        switch (role) {
          case 'super_admin':
            return 'Super Admin';
          case 'support':
            return 'Support';
          case 'billing_ops':
            return 'Billing Ops';
          case 'readonly':
            return 'Read-only';
        }
      },
      signOutAriaLabel: 'Sign out',
    },
  },
};

export const useCommonContent = (): CommonContent => useBilingual(COMMON_CONTENT);
